"use client";

import { useMemo, type ReactNode } from "react";
import { FolderEditInput } from "./FolderEditInput";
import { FolderRow, type FolderTreeScope } from "./FolderRow";
import { buildFolderTree, groupWorkersByFolder } from "./folderTreeModel";
import type { SidebarFolder, SidebarWorker } from "./types";
import type { FolderEditor } from "./useFolderEditing";
import type { FolderExpansion } from "./useFolderExpansion";
import { useWorkerMoving } from "./useWorkerMoving";
import { WorkerRow } from "./WorkerRow";

/** The automation panel: departments nested with their workers, plus the loose workers. */
export function FolderTree({
  header,
  folders,
  workers,
  pathname,
  onCloseDrawer,
  expansion,
  editor,
  onMoved,
}: {
  header?: ReactNode;
  /** Already scoped to the active business. */
  folders: SidebarFolder[];
  /** Already scoped to the active business. */
  workers: SidebarWorker[];
  pathname: string;
  onCloseDrawer: () => void;
  expansion: FolderExpansion;
  editor: FolderEditor;
  onMoved?: () => Promise<void>;
}) {
  const tree = useMemo(() => buildFolderTree(folders), [folders]);
  const workersByFolder = useMemo(
    () => groupWorkersByFolder(workers, folders),
    [workers, folders]
  );
  const rootWorkers = workersByFolder.get("") ?? [];
  const movement = useWorkerMoving({ workers, folders, onMoved, expand: expansion.expand });
  const scope: FolderTreeScope = { pathname, onCloseDrawer, workersByFolder, expansion, editor, movement };

  return (
    <nav aria-label="Worker folders" className="flex min-h-0 flex-1 flex-col overflow-hidden px-2 pb-4 pt-1">
      {header}
      <section aria-label="Automation workers" tabIndex={0} className="min-h-0 flex-1 space-y-0.5 overflow-y-auto overscroll-contain rounded-lg focus-visible:outline-2 focus-visible:outline-violet-400 focus-visible:-outline-offset-2">
        {movement.error && <p role="alert" className="px-2 py-1 text-xs text-red-300">{movement.error}</p>}
        {movement.moving && <p role="status" className="px-2 py-1 text-xs text-zinc-400">Moving worker…</p>}
        {editor.editing?.mode === "create" && editor.editing.parentId === null && (
          <FolderEditInput
            depth={0}
            value={editor.editValue}
            onChange={editor.setEditValue}
            onCommit={editor.commit}
            onCancel={editor.cancel}
          />
        )}
        {tree.map((node) => (
          <FolderRow key={node.id} node={node} depth={0} scope={scope} />
        ))}
        <div {...movement.folderProps(null)} data-folder-drop="root" data-worker-root="true" className={`rounded-lg data-[drop-active=true]:bg-violet-500/15 data-[drop-active=true]:ring-1 data-[drop-active=true]:ring-violet-400 ${movement.dragged ? "min-h-9" : ""}`}>
          {rootWorkers.map((w) => (
            <WorkerRow
              key={w.id}
              worker={w}
              depth={0}
              pathname={pathname}
              onCloseDrawer={onCloseDrawer}
              movement={movement}
            />
          ))}
        </div>
        {folders.length === 0 && workers.length === 0 && (
          <p className="px-2 py-1 text-sm text-zinc-500">No workers yet.</p>
        )}
      </section>
    </nav>
  );
}
